import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowLeft, FileText } from "lucide-react";
import { formatDate } from "@/utils/formatDate";
import { PageHeader } from "@/components/PageHeader";
import { NeonCard } from "@/components/NeonCard";
import { getPublishedPostBySlug } from "@/lib/public-content.functions";
import { sanitizeArticleHtml } from "@/lib/sanitizeArticleHtml";
import { buildMeta } from "@/lib/seo";
import { errorMessage } from "@/lib/error-capture";

export const Route = createFileRoute("/blog_/$slug")({
  loader: ({ params }) =>
    getPublishedPostBySlug({ data: { type: "blog", slug: params.slug } }),
  head: ({ loaderData, params }) => {
    if (!loaderData) {
      return buildMeta({
        title: "Artikel nicht gefunden - Blog",
        description: "Der gesuchte Blog-Eintrag existiert nicht (mehr).",
        path: `/blog/${params.slug}`,
        noindex: true,
      });
    }
    const base = buildMeta({
      title: `${loaderData.title} - Blog`,
      description: loaderData.excerpt,
      path: `/blog/${params.slug}`,
      type: "article",
      image: "https://restlicht.fyi/og-blog.jpg",
    });
    return {
      ...base,
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            headline: loaderData.title,
            description: loaderData.excerpt,
            datePublished: loaderData.published_date,
            image: "https://restlicht.fyi/og-blog.jpg",
            inLanguage: "de",
            mainEntityOfPage: `https://restlicht.fyi/blog/${params.slug}`,
            author: { "@type": "Organization", name: "Restlicht.FYI" },
            publisher: { "@type": "Organization", name: "Restlicht.FYI" },
          }),
        },
      ],
    };
  },

  errorComponent: ({ error }) => (
    <div role="alert" style={{ padding: "2rem", color: "var(--text-secondary)" }}>
      Fehler: {errorMessage(error)}
    </div>
  ),
  notFoundComponent: () => (
    <div style={{ padding: "2rem" }}>Artikel nicht gefunden.</div>
  ),
  component: BlogDetail,
});

function BlogDetail() {
  const post = Route.useLoaderData();

  if (!post) {
    return (
      <div data-section="blog">
        <PageHeader icon={<FileText size={28} />} title="Artikel nicht gefunden" />
        <NeonCard style={{ padding: "1.5rem" }}>
          <p style={{ color: "var(--text-secondary)", marginBottom: "1rem" }}>
            Der gesuchte Blog-Eintrag existiert nicht (mehr).
          </p>
          <Link
            to="/blog"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "0.4rem",
              color: "var(--accent)",
              textDecoration: "none",
              fontWeight: 600,
            }}
          >
            <ArrowLeft size={16} /> Zurück zum Blog
          </Link>
        </NeonCard>
      </div>
    );
  }

  return (
    <div data-section="blog">
      <PageHeader icon={<FileText size={28} />} title={post.title} />

      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1rem", flexWrap: "wrap", gap: "0.5rem" }}>
        <span style={{ color: "var(--text-secondary)", fontSize: "0.9rem" }}>
          {formatDate(post.published_date)}
        </span>
        <span
          style={{
            background: "var(--accent-bg)",
            color: "var(--accent)",
            border: "1px solid var(--accent-border)",
            borderRadius: "20px",
            padding: "0.2rem 0.6rem",
            fontSize: "0.8rem",
            textTransform: "uppercase",
            letterSpacing: "0.05em",
          }}
        >
          Blog
        </span>
      </div>

      <NeonCard as="article" style={{ padding: "1.75rem" }}>
        <div
          className="article-content"
          dangerouslySetInnerHTML={{ __html: sanitizeArticleHtml(post.content) }}
        />
      </NeonCard>

      <div style={{ marginTop: "1.5rem" }}>
        <Link
          to="/blog"
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "0.4rem",
            color: "var(--accent)",
            textDecoration: "none",
            fontWeight: 600,
          }}
        >
          <ArrowLeft size={16} /> Zurück zum Blog
        </Link>
      </div>
    </div>
  );
}
